import React, { useCallback, useEffect, useState } from 'react';
import { FaBars, FaChevronDown, FaFacebookF, FaInstagram, FaTimes, FaYoutube } from 'react-icons/fa';
import {
  BookButton,
  ChartersTrigger,
  ChevronIcon,
  CloseButton,
  DropdownColumn,
  DropdownHeader,
  DropdownLink,
  DropdownPanel,
  DropdownWrapper,
  HeaderContainer,
  HeaderSocialIcon,
  HeaderSocialLinks,
  Logo,
  MenuButton,
  MobileSubHeader,
  MobileSubLink,
  MobileSubNav,
  NavContainer,
  NavItem,
  NavLinks
} from './styles/Header.styles';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [chartersDropdown, setChartersDropdown] = useState(false);
  const [chartersExpanded, setChartersExpanded] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => {
    setIsMenuOpen(false);
    setChartersExpanded(false);
  };

  const closeDropdown = useCallback(() => {
    setChartersDropdown(false);
  }, []);

  const handleDropdownLinkClick = () => {
    closeDropdown();
    closeMenu();
  };

  const toggleChartersExpanded = (e: React.MouseEvent) => {
    e.preventDefault();
    setChartersExpanded((prev) => !prev);
  };

  return (
    <HeaderContainer $scrolled={scrolled}>
      <NavContainer>
        <Logo to="/" onClick={closeMenu}>FUSSAKI</Logo>

        <MenuButton onClick={toggleMenu} aria-label="Toggle menu">
          {FaBars({ size: 22 })}
        </MenuButton>

        <NavLinks $isOpen={isMenuOpen}>
          <CloseButton onClick={closeMenu}>
            {FaTimes({ size: 24 })}
          </CloseButton>
          <NavItem to="/" onClick={closeMenu}>Home</NavItem>
          <NavItem to="/about" onClick={closeMenu}>About</NavItem>

          {/* Desktop: hover dropdown | Mobile: accordion */}
          <DropdownWrapper
            onMouseEnter={() => setChartersDropdown(true)}
            onMouseLeave={() => setChartersDropdown(false)}
          >
            {/* Desktop trigger */}
            <ChartersTrigger>
              <NavItem to="/charters" onClick={handleDropdownLinkClick}>
                Charters
              </NavItem>
              <ChevronIcon $open={chartersDropdown} className="desktop-only">
                {FaChevronDown({ size: 10 })}
              </ChevronIcon>
            </ChartersTrigger>

            {/* Desktop dropdown panel */}
            <DropdownPanel $visible={chartersDropdown}>
              <DropdownColumn>
                <DropdownHeader to="/charters/spearfishing" onClick={handleDropdownLinkClick}>
                  Spearfishing
                </DropdownHeader>
                <DropdownLink to="/charters/spearfishing/catalina" onClick={handleDropdownLinkClick}>
                  Catalina Island
                </DropdownLink>
                <DropdownLink to="/charters/spearfishing/san-clemente-island" onClick={handleDropdownLinkClick}>
                  San Clemente Island
                </DropdownLink>
                <DropdownLink to="/charters/spearfishing/open-blue-water" onClick={handleDropdownLinkClick}>
                  Open Blue Water
                </DropdownLink>
                <DropdownLink to="/charters/spearfishing/kelp-forests" onClick={handleDropdownLinkClick}>
                  Kelp Forests
                </DropdownLink>
                <DropdownLink to="/charters/spearfishing/reefs" onClick={handleDropdownLinkClick}>
                  Reefs &amp; Structure
                </DropdownLink>
              </DropdownColumn>

              <DropdownColumn>
                <DropdownHeader to="/charters" onClick={handleDropdownLinkClick}>
                  More Charters
                </DropdownHeader>
                <DropdownLink to="/charters/sportfishing" onClick={handleDropdownLinkClick}>
                  Sportfishing
                </DropdownLink>
                <DropdownLink to="/charters/private" onClick={handleDropdownLinkClick}>
                  Private Charters
                </DropdownLink>
              </DropdownColumn>
            </DropdownPanel>

            {/* Mobile accordion toggle */}
            <ChevronIcon
              $open={chartersExpanded}
              className="mobile-only"
              onClick={toggleChartersExpanded}
              style={{ padding: '0.2rem', cursor: 'pointer' }}
            >
              {FaChevronDown({ size: 12 })}
            </ChevronIcon>

            {/* Mobile sub-nav accordion */}
            <MobileSubNav $expanded={chartersExpanded}>
              <MobileSubHeader to="/charters/spearfishing" onClick={handleDropdownLinkClick}>
                Spearfishing
              </MobileSubHeader>
              <MobileSubLink to="/charters/spearfishing/catalina" onClick={handleDropdownLinkClick}>
                Catalina Island
              </MobileSubLink>
              <MobileSubLink to="/charters/spearfishing/san-clemente-island" onClick={handleDropdownLinkClick}>
                San Clemente Island
              </MobileSubLink>
              <MobileSubLink to="/charters/spearfishing/open-blue-water" onClick={handleDropdownLinkClick}>
                Open Blue Water
              </MobileSubLink>
              <MobileSubLink to="/charters/spearfishing/kelp-forests" onClick={handleDropdownLinkClick}>
                Kelp Forests
              </MobileSubLink>
              <MobileSubLink to="/charters/spearfishing/reefs" onClick={handleDropdownLinkClick}>
                Reefs &amp; Structure
              </MobileSubLink>
              <MobileSubHeader to="/charters" onClick={handleDropdownLinkClick}>
                More Charters
              </MobileSubHeader>
              <MobileSubLink to="/charters/sportfishing" onClick={handleDropdownLinkClick}>
                Sportfishing
              </MobileSubLink>
              <MobileSubLink to="/charters/private" onClick={handleDropdownLinkClick}>
                Private Charters
              </MobileSubLink>
            </MobileSubNav>
          </DropdownWrapper>

          <NavItem to="/gallery" onClick={closeMenu}>Gallery</NavItem>
          <NavItem to="/contact" onClick={closeMenu}>Contact</NavItem>
          <BookButton to="/book" onClick={closeMenu}>Book Now</BookButton>
          <HeaderSocialLinks>
            <HeaderSocialIcon href="https://www.instagram.com/fussaki/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              {FaInstagram({ size: 16 })}
            </HeaderSocialIcon>
            <HeaderSocialIcon href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              {FaFacebookF({ size: 14 })}
            </HeaderSocialIcon>
            <HeaderSocialIcon href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              {FaYoutube({ size: 16 })}
            </HeaderSocialIcon>
          </HeaderSocialLinks>
        </NavLinks>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header;
